import { API_BASE } from '../config.js';
import { socketClient } from './socket.js';

const RECHECK_MS = 30_000;
const PING_TIMEOUT_MS = 5_000;
const STARTUP_PING_ATTEMPTS = 5;
const STARTUP_PING_DELAY_MS = 1_000;

let offline = false;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let toastEl: HTMLElement | null = null;

export async function pingServer(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/health`, {
      cache: 'no-store',
      signal: AbortSignal.timeout(PING_TIMEOUT_MS),
    });
    if (!res.ok) return false;
    const data = (await res.json()) as { status?: string };
    return data.status === 'ok';
  } catch {
    return false;
  }
}

async function pingServerWithRetry(
  attempts = STARTUP_PING_ATTEMPTS,
  delayMs = STARTUP_PING_DELAY_MS
): Promise<boolean> {
  for (let i = 0; i < attempts; i++) {
    if (await pingServer()) return true;
    if (i < attempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
  return false;
}

function ensureOfflineToast(): HTMLElement {
  if (toastEl?.isConnected) return toastEl;
  toastEl = document.createElement('div');
  toastEl.id = 'server-offline-toast';
  toastEl.className = 'toast toast--error toast--persistent';
  toastEl.setAttribute('role', 'alert');
  toastEl.textContent = 'Connexion au serveur impossible. Nouvelle tentative dans 30 secondes…';
  document.body.appendChild(toastEl);
  return toastEl;
}

function hideOfflineToast(): void {
  toastEl?.remove();
  toastEl = null;
}

function stopPolling(): void {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

function startPolling(): void {
  if (pollTimer) return;
  pollTimer = setInterval(() => {
    void tryRecover();
  }, RECHECK_MS);
}

async function tryRecover(): Promise<void> {
  if (!(await pingServer())) return;
  stopPolling();
  hideOfflineToast();
  offline = false;
  if (!socketClient.connected) {
    socketClient.connect();
  }
  window.location.reload();
}

/** Signale une indisponibilité serveur et lance la surveillance (30 s). */
export function reportServerOffline(): void {
  if (offline) return;
  offline = true;
  ensureOfflineToast();
  startPolling();
}

async function confirmServerOffline(): Promise<void> {
  if (!(await pingServer())) {
    reportServerOffline();
  }
}

export function initServerWatchdog(): void {
  void pingServerWithRetry().then((ok) => {
    if (!ok) reportServerOffline();
  });

  socketClient.on('connect_error', () => {
    void confirmServerOffline();
  });

  socketClient.on('disconnect', (reason) => {
    if (reason === 'io server disconnect') return;
    void confirmServerOffline();
  });
}
